import { ApiFehler, type Fehlerantwort, verbindungsfehler } from './fehler';

/*
 * DIE VERSION DER SCHNITTSTELLE STEHT IM PFAD (WEB-01, BUC-16).
 *
 * `v1` ist eine Zusage an jeden, der gegen diese API baut — vor allem an das
 * Portalteam (E-02, ein eigenes Projekt). Sie wechselt nur bei einem Bruch,
 * mit Ankündigung und Bestandsschutz nach BUC-16.
 *
 * NICHT zu verwechseln mit dem Systemstand (`deploy/stand.mjs`): Der ändert
 * sich bei jeder Auslieferung und sagt, was auf dem Server liegt.
 */
const WURZEL = '/api/v1';
const SCHLUESSEL_ABLAGE = 'crm.sitzungsschluessel';

/**
 * Wo der Sitzungsschluessel liegt.
 *
 * sessionStorage und nicht localStorage: Der Schluessel verschwindet mit dem
 * Schliessen des Tabs. Auf einem Rechner, an dem mehrere arbeiten — Empfang,
 * Werkstatt —, ist das der Unterschied zwischen "abgemeldet" und "der
 * naechste arbeitet unter meinem Namen weiter".
 *
 * Ein httpOnly-Cookie waere staerker: Er waere fuer JavaScript unlesbar und
 * damit gegen eingeschleusten Code geschuetzt. Er verlangt aber einen Schutz
 * gegen Anfragefaelschung (CSRF) und eine Aenderung an der API. Solange die
 * Oberflaeche mit der API auf demselben Ursprung liegt — im Entwicklungsbetrieb
 * ueber den Vite-Weiterleiter, spaeter hinter Caddy —, ist der Wechsel
 * jederzeit nachholbar. Er gehoert vor die erste Auslieferung.
 */
export const schluesselAblage = {
  lesen(): string | null {
    try {
      return sessionStorage.getItem(SCHLUESSEL_ABLAGE);
    } catch {
      // Private Fenster und gesperrte Speicher werfen hier. Ohne Ablage
      // funktioniert die Anmeldung fuer die Dauer der Seite trotzdem.
      return null;
    }
  },
  setzen(schluessel: string): void {
    try {
      sessionStorage.setItem(SCHLUESSEL_ABLAGE, schluessel);
    } catch {
      /* siehe oben */
    }
  },
  loeschen(): void {
    try {
      sessionStorage.removeItem(SCHLUESSEL_ABLAGE);
    } catch {
      /* siehe oben */
    }
  },
};

/** Wird gerufen, wenn die API eine Sitzung ablehnt — die Oberflaeche raeumt dann auf. */
let beiAbgelaufenerSitzung: (() => void) | undefined;

export function meldeSitzungsende(rueckruf: () => void): void {
  beiAbgelaufenerSitzung = rueckruf;
}

type Aufrufoptionen = {
  /**
   * PUT steht seit dem Produktkatalog dabei: Der Staffelsatz eines Preises
   * wird als GANZES ersetzt (PRD-14). Monotonie und Vollstaendigkeit sind
   * Eigenschaften des Satzes, und wer zeilenweise pflegt, hat zwischendurch
   * immer einen Satz, der beides verletzt — waehrend die Preisauskunft
   * rechnet.
   */
  readonly verfahren?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly rumpf?: unknown;
  /** Fuer die Anmeldung selbst: dort gibt es noch keinen Schluessel. */
  readonly ohneSchluessel?: boolean;
  /**
   * Die Anmeldung eines GERAETS statt einer Sitzung — das Check-in-Tablet
   * (TAG-49) schickt `Geraet <kennung>:<schluessel>`. Nur zusammen mit
   * `ohneSchluessel`.
   */
  readonly geraet?: string;
  /** NFA-01 (CRM-165): bricht den Aufruf ab, etwa bei „Erneut versuchen" der Ladeanzeige. */
  readonly signal?: AbortSignal;
  /**
   * BEN-07 (AP-SQ-03): ein Abruf im Takt, nicht von Hand. Er traegt
   * `x-hintergrund: 1` und haelt die Sitzung nicht wach — sonst meldet die
   * Leerlaufgrenze eine offen gelassene Arbeitsflaeche nie ab. Nur fuer die
   * Wiederholungen eines Takts, nicht fuer den ersten Abruf beim Oeffnen.
   */
  readonly hintergrund?: boolean;
};

/**
 * Der einzige Weg zur API.
 *
 * Er tut drei Dinge, die sonst an jeder Aufrufstelle einzeln stuenden und
 * genau dort vergessen wuerden: den Schluessel anhaengen, jede Fehlerantwort
 * in einen ApiFehler mit den drei Teilen aus ERR-01 verwandeln, und ein
 * abgelaufene Sitzung an einer Stelle behandeln statt an zwanzig.
 */
/**
 * Wurzel und Anmeldekopf fuer Antworten, die KEIN JSON sind.
 *
 * Ein PDF laesst sich nicht durch `rufe` holen — es kaeme durch `JSON.parse`.
 * Damit trotzdem nur EINE Stelle weiss, wo die API steht und wo der
 * Schluessel liegt, geben diese beiden Helfer es heraus, statt dass die
 * Aufrufer es nachbauen. Ein zweiter Ort fuer den Schluessel waere der Ort,
 * an dem er beim naechsten Umbau vergessen wird.
 */
export function basisUrl(): string {
  return WURZEL;
}

export function anmeldekopf(): Record<string, string> {
  const schluessel = schluesselAblage.lesen();
  return schluessel === null ? {} : { authorization: `Bearer ${schluessel}` };
}

export async function rufe<T>(pfad: string, optionen: Aufrufoptionen = {}): Promise<T> {
  const kopf: Record<string, string> = { accept: 'application/json' };
  if (optionen.rumpf !== undefined) kopf['content-type'] = 'application/json';

  if (optionen.geraet !== undefined) kopf['authorization'] = `Geraet ${optionen.geraet}`;
  if (optionen.ohneSchluessel !== true) {
    const schluessel = schluesselAblage.lesen();
    if (schluessel !== null) kopf['authorization'] = `Bearer ${schluessel}`;
  }
  // BEN-07: Same-origin hinter Caddy — der eigene Kopf braucht keine CORS-Freigabe.
  if (optionen.hintergrund === true) kopf['x-hintergrund'] = '1';

  let antwort: Response;
  try {
    antwort = await fetch(`${WURZEL}${pfad}`, {
      method: optionen.verfahren ?? 'GET',
      headers: kopf,
      ...(optionen.signal !== undefined ? { signal: optionen.signal } : {}),
      ...(optionen.rumpf !== undefined ? { body: JSON.stringify(optionen.rumpf) } : {}),
    });
  } catch (ursache) {
    throw verbindungsfehler(ursache);
  }

  if (antwort.status === 204) return undefined as T;

  // Lesen UND Deuten gehoeren in den Schutz. Stand JSON.parse ausserhalb,
  // verliess ein nackter SyntaxError diese Funktion — und der Kommentar oben
  // versprach das Gegenteil. Getroffen haette es genau den Fall, den der Code
  // weiter unten beschreibt: eine Antwort von einem Zwischenglied, etwa eine
  // HTML-Fehlerseite von Caddy. Statt einer Meldung mit drei Teilen haette der
  // Anwender eine weisse Seite gesehen.
  let daten: unknown;
  let lesbar = true;
  try {
    const text = await antwort.text();
    daten = text === '' ? undefined : JSON.parse(text);
  } catch {
    lesbar = false;
  }

  if (antwort.ok && lesbar) return daten as T;

  // Alles, was die API ablehnt, traegt das Format aus @crm/kontrakte. Ein
  // anderer Rumpf kaeme von einem Zwischenglied — Caddy, ein Weiterleiter —
  // und ist fuer den Anwender genauso ein Fehler.
  // Ein unlesbarer Rumpf bei ok-Status ist genauso ein Fehler wie ein
  // Fehlerstatus: Die Oberflaeche kann damit nichts anfangen.
  const fehler = lesbar && istFehlerantwort(daten)
    ? new ApiFehler(daten, antwort.status)
    : new ApiFehler(
        {
          kennung: 'unerwartete_antwort',
          meldung: {
            // Der Rumpf wird ausdruecklich NICHT uebernommen: Er kann alles
            // enthalten, auch Daten Dritter (ERR-12).
            wasGeschah: `Das System hat unerwartet geantwortet (Status ${antwort.status}).`,
            auswirkung: 'Der Vorgang wurde nicht ausgefuehrt.',
            naechsterSchritt:
              'Versuchen Sie es erneut. Bleibt es dabei, wenden Sie sich an die Administration.',
          },
          vorgang:
            antwort.headers.get('x-vorgang') ?? '00000000-0000-4000-8000-000000000000',
          verstoesse: [],
        },
        antwort.status,
      );

  // Eine abgelaufene oder fremde Sitzung fuehrt ueberall zum selben: zurueck
  // zur Anmeldung. Das hier zu tun ist der Unterschied zu zwanzig Stellen,
  // von denen eine es vergisst.
  if (fehler.kennung === 'sitzung_abgelaufen' || fehler.kennung === 'nicht_angemeldet') {
    schluesselAblage.loeschen();
    beiAbgelaufenerSitzung?.();
  }

  throw fehler;
}

function istFehlerantwort(daten: unknown): daten is Fehlerantwort {
  if (typeof daten !== 'object' || daten === null) return false;
  const k = daten as Partial<Fehlerantwort>;
  return (
    typeof k.kennung === 'string'
    && typeof k.vorgang === 'string'
    && typeof k.meldung === 'object'
    && k.meldung !== null
    && typeof k.meldung.wasGeschah === 'string'
  );
}
